import type {
  FinancialChargeRole,
  FinancialGroupChargeRow,
  FinancialGroupPriceRow,
  FinancialGroupReview,
  FinancialLine,
  FinancialLineState,
} from '@/types/Models/Financial'
import { subtractFinancialAmounts, sumFinancialAmounts } from '@/services/FinancialAmounts'
import {
  financialChargeAmount,
  financialInvoiceLabel,
  financialLocalDate,
  financialReady,
  financialRole,
} from '@/services/FinancialImport'

const sum = (values: (string | null)[]): string | null =>
  values.some((value) => value === null) ? null : sumFinancialAmounts(values as string[])

// The customer amount a row is billed at: the approved amount, or its PacSort price while it is open.
const priceOf = (line: FinancialLine, state: FinancialLineState): string | null =>
  financialReady(line.batch, line.row) ? line.row.review!.customerPrice! : (state.price?.amount ?? null)

const basisOf = (line: FinancialLine, state: FinancialLineState): string => {
  const review = line.row.review
  const source = review?.status === 'approved' ? review.priceSource : state.price?.source
  const percent = review?.status === 'approved' ? review.marginPercent : state.price?.marginPercent
  if (source === 'booked') return 'PacSort booked price'
  if (source === 'margin') return `Cost + ${percent ?? '?'}% margin`
  if (source === 'manual') return 'Entered by reviewer'
  // Approved before the price basis was recorded (before 11 October).
  return review?.status === 'approved' ? 'Approved amount' : 'No price'
}

const range = (values: string[]): [string, string] => {
  const sorted = values.filter(Boolean).sort()
  return [sorted[0] ?? '', sorted[sorted.length - 1] ?? '']
}

// Summarises the rows of one customer for review before they are approved. Excluded rows are left out.
export const financialGroupReview = (
  lines: FinancialLine[],
  stateOf: (line: FinancialLine) => FinancialLineState,
): FinancialGroupReview => {
  const active = lines.filter((line) => line.row.review?.status !== 'excluded')
  const charges = new Map<string, { description: string; role: FinancialChargeRole; lines: FinancialLine[] }>()
  const deliveries = new Map<string, FinancialLine[]>()
  const shipments = new Set<string>()
  const attention = new Map<string, number>()
  for (const line of active) {
    const state = stateOf(line)
    if (state.reason) attention.set(state.reason, (attention.get(state.reason) ?? 0) + 1)
    if (line.row.match?.itemId) shipments.add(line.row.match.itemId)
    // A row marked not to bill is left out of the prices like an excluded row, so it never reads as a missing price.
    if (state.exclude) continue
    const role = financialRole(line.batch, line.row)
    const key = `${role}:${line.row.description}`
    const charge = charges.get(key) ?? { description: line.row.description, role, lines: [] }
    charge.lines.push(line)
    charges.set(key, charge)
    // Every priced delivery belongs to a price level, so the levels add up to the priced deliveries.
    const price = priceOf(line, state)
    if (role === 'base' && price !== null) {
      const level = `${price}:${line.row.match?.service ?? ''}`
      deliveries.set(level, [...(deliveries.get(level) ?? []), line])
    }
  }
  const chargeRows: FinancialGroupChargeRow[] = [...charges.values()]
    .map((charge) => {
      const prices = charge.lines.map((line) => priceOf(line, stateOf(line)))
      const priced = charge.lines.filter((_, index) => prices[index] !== null)
      const cost = sum(priced.map((line) => financialChargeAmount(line.batch, line.row)))
      const price = sumFinancialAmounts(prices.filter((value): value is string => value !== null))
      return {
        description: charge.description,
        role: charge.role,
        rows: charge.lines.length,
        cost: sum(charge.lines.map((line) => financialChargeAmount(line.batch, line.row))),
        price,
        unpriced: charge.lines.length - priced.length,
        margin: cost === null || !priced.length ? null : subtractFinancialAmounts(price, cost),
        basis: [...new Set(charge.lines.map((line) => basisOf(line, stateOf(line))))].join(' · '),
      }
    })
    .sort((a, b) => Number(a.role !== 'base') - Number(b.role !== 'base') || b.rows - a.rows)
  const priceRows: FinancialGroupPriceRow[] = [...deliveries.values()]
    .map((group) => {
      const grams = group
        .map((line) => line.row.match?.weightGrams ?? null)
        .filter((value): value is number => value !== null)
      const price = priceOf(group[0], stateOf(group[0]))!
      const cost = sum(group.map((line) => financialChargeAmount(line.batch, line.row)))
      const total = sumFinancialAmounts(group.map(() => price))
      return {
        price,
        basis: [...new Set(group.map((line) => basisOf(line, stateOf(line))))].join(' · '),
        service: group[0].row.match?.service ?? '',
        minGrams: grams.length ? Math.min(...grams) : null,
        maxGrams: grams.length ? Math.max(...grams) : null,
        shipments: group.length,
        cost,
        total,
        margin: cost === null ? null : subtractFinancialAmounts(total, cost),
      }
    })
    .sort((a, b) => b.shipments - a.shipments || Number(a.price) - Number(b.price))
  const files = new Map<string, FinancialGroupReview['files'][number]>()
  for (const line of lines) {
    const file = files.get(line.batch.id) ?? {
      id: line.batch.id,
      label: `${line.batch.carrier} ${financialInvoiceLabel(line.batch)}`,
      fileName: line.batch.fileName,
      createdAt: line.batch.createdAt,
      createdByName: line.batch.createdByName,
      rows: 0,
      excluded: 0,
    }
    file.rows += 1
    if (line.row.review?.status === 'excluded') file.excluded += 1
    files.set(line.batch.id, file)
  }
  const [bookedFrom, bookedTo] = range(active.map((line) => (line.row.match ? financialLocalDate(line.row.match.createdAt) : '')))
  const [chargeFrom, chargeTo] = range(active.map((line) => line.row.date))
  return {
    shipments: shipments.size,
    rows: active.length,
    bookedFrom,
    bookedTo,
    chargeFrom,
    chargeTo,
    charges: chargeRows,
    prices: priceRows,
    attention: [...attention].map(([reason, rows]) => ({ reason, rows })).sort((a, b) => b.rows - a.rows),
    files: [...files.values()],
  }
}
